import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore, type ToolItem } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_GRID_DIMENSION,
  DEFAULT_LEARNING_RATE,
  DEFAULT_NUM_ITERATIONS,
  DEFAULT_RANDOM_ORDER,
  DEFAULT_SIGMA,
  INPUT_NAMES,
} from '@/tools/selfOrganizingMapsV2/selfOrganizingMaps.constants';
import type { BaseToolPersistedState, ModelRehydratePayload, ToolParameter } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface DurationItem {
  value: number;
  units: string;
}

interface SelfOrganizingMapsState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // computed property using monkey()
  modelPropertiesExpanded: boolean;
  color: string;
  anomalyThreshold: number;
  sigma: number;
  learningRate: number;
  gridDimension: number | '';
  numIterations: number | '';
  randomOrder: boolean;
  cleanCapsules: boolean;
  capsuleMinDuration: DurationItem;
  capsuleMergeDuration: DurationItem;
  tableApplicationId?: string;
  instanceId?: string;
}

export type SelfOrganizingMapsDehydratedState = Omit<SelfOrganizingMapsState, 'window'>;

export class SelfOrganizingMapsStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqSelfOrganizingMapsStoreV2';
  type = TREND_TOOLS.SELF_ORGANIZING_MAPS_V_2;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) => parameter?.name?.startsWith('signal'),
      multiple: true,
    },
  };

  initialize() {
    this.state = this.immutable({
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
      sigma: DEFAULT_SIGMA,
      learningRate: DEFAULT_LEARNING_RATE,
      gridDimension: DEFAULT_GRID_DIMENSION,
      numIterations: DEFAULT_NUM_ITERATIONS,
      randomOrder: DEFAULT_RANDOM_ORDER,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
      tableApplicationId: undefined,
      instanceId: undefined,
    });
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window() {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get anomalyThreshold(): number {
    return this.state.get('anomalyThreshold');
  }

  get sigma(): number {
    return this.state.get('sigma');
  }

  get learningRate(): number {
    return this.state.get('learningRate');
  }

  get gridDimension(): number {
    return this.state.get('gridDimension');
  }

  get numIterations(): number {
    return this.state.get('numIterations');
  }

  get randomOrder(): boolean {
    return this.state.get('randomOrder');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration() {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration() {
    return this.state.get('capsuleMergeDuration');
  }

  get tableApplicationId() {
    return this.state.get('tableApplicationId');
  }

  get instanceId() {
    return this.state.get('instanceId');
  }

  dehydrate(): SelfOrganizingMapsDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<SelfOrganizingMapsDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }) {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setAnomalyThreshold(payload: { anomalyThreshold: number }): void {
    this.state.set('anomalyThreshold', payload.anomalyThreshold);
  }

  setSigma(payload: { sigma: number }): void {
    this.state.set('sigma', payload.sigma);
  }

  setLearningRate(payload: { learningRate: number }): void {
    this.state.set('learningRate', payload.learningRate);
  }

  setGridDimension(payload: { gridDimension: number }): void {
    this.state.set('gridDimension', payload.gridDimension);
  }

  setNumIterations(payload: { numIterations: number }): void {
    this.state.set('numIterations', payload.numIterations);
  }

  setRandomOrder(payload: { randomOrder: boolean }): void {
    this.state.set('randomOrder', payload.randomOrder);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  modifyConfigParams<TConfig extends Record<string, unknown>>(config: TConfig): TConfig {
    delete (config as Record<string, unknown>)['modelPropertiesExpanded'];
    return config;
  }

  modelToolRehydrateForEdit = (payload: ModelRehydratePayload): void => {
    if (payload.type !== this.type) {
      return;
    }
    this.initialize();
    this.state.set('id', payload.id);
    this.state.set('instanceId', payload.instanceId);
    this.state.set('tableApplicationId', payload.instanceData?.tableApplicationId);
    (payload.modelData?.inputFeatures ?? []).forEach((feature: any): void => {
      const inputName: string = feature.name;
      switch (inputName) {
        case SeeqNames.Models.InstanceNameInputFeatureName:
          // TODO: CRAB-53864: Handle duplicate names more gracefully
          this.state.set('name', payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`);
          break;
        case INPUT_NAMES.anomalyThresholdInput:
          this.state.set('anomalyThreshold', feature.inputNumeric ?? DEFAULT_ANOMALY_THRESHOLD);
          break;
        case INPUT_NAMES.sigmaInput:
          this.state.set('sigma', feature.inputNumeric ?? DEFAULT_SIGMA);
          break;
        case INPUT_NAMES.learningRateInput:
          this.state.set('learningRate', feature.inputNumeric ?? DEFAULT_LEARNING_RATE);
          break;
        case INPUT_NAMES.gridDimensionInput:
          this.state.set('gridDimension', feature.inputNumeric ?? DEFAULT_GRID_DIMENSION);
          break;
        case INPUT_NAMES.numIterationsInput:
          this.state.set('numIterations', feature.inputNumeric ?? DEFAULT_NUM_ITERATIONS);
          break;
        case INPUT_NAMES.randomOrderInput:
          this.state.set('randomOrder', feature.inputBoolean ?? DEFAULT_RANDOM_ORDER);
          break;
        case INPUT_NAMES.startTimeInput: {
          const startTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowStart', moment(startTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.endTimeInput: {
          const endTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowEnd', moment(endTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.mergeCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMergeDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.minCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMinDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.trainWithinConditionInput:
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('condition', _.pick(item, ['id', 'name']));
          }
          break;

        case INPUT_NAMES.restrictOutputToConditionInput: {
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('restrictToCondition', _.pick(item, ['id', 'name']));
          }
          break;
        }
        default:
          if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
            const existingSignals = this.state.get('inputSignals') || [];
            const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('inputSignals', [...existingSignals, _.pick(item, ['id', 'name'])]);
          }
          break;
      }
    });
  };

  protected readonly handlers = {
    ...this.baseHandlers,
    SOM_SET_TRAINING_WINDOW: this.setTrainingWindow,
    SOM_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    SOM_SET_ANOMALY_THRESHOLD: this.setAnomalyThreshold,
    SOM_SET_SIGMA: this.setSigma,
    SOM_SET_LEARNING_RATE: this.setLearningRate,
    SOM_SET_GRID_DIMENSION: this.setGridDimension,
    SOM_SET_NUM_ITERATIONS: this.setNumIterations,
    SOM_SET_RANDOM_ORDER: this.setRandomOrder,
    SOM_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    SOM_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    SOM_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
    MODEL_TOOL_REHYDRATE_FOR_EDIT: this.modelToolRehydrateForEdit,
  };
}
